import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import { CodedDocumentKey, Trip, TripSearchCommand, TripSummary, TripUpdate } from '../models/trip/trip';
import { PagedResult } from './bank-account.proxy.service';

@Injectable({ providedIn: 'root' })
export class TripProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/trip';

  public search(
    command: TripSearchCommand,
    pageNumber: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: number,
  ): Observable<ApiResponse<PagedResult<Trip>>> {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize).set('sortOrder', sortOrder);
    if (sortField) params = params.set('sortField', sortField);
    return this.httpClient.post<ApiResponse<PagedResult<Trip>>>(`${this.baseUrl}/search`, command, { params });
  }

  public getSummary(): Observable<ApiResponse<TripSummary>> {
    return this.httpClient.get<ApiResponse<TripSummary>>(`${this.baseUrl}/summary`);
  }

  public getById(id: string): Observable<ApiResponse<Trip>> {
    return this.httpClient.get<ApiResponse<Trip>>(`${this.baseUrl}/${id}`);
  }

  /** Standard employees with active accounting matching the typed text (the trip form's typeahead). */
  public getEmployees(term: string): Observable<ApiResponse<CodedDocumentKey[]>> {
    return this.httpClient.get<ApiResponse<CodedDocumentKey[]>>(`${this.baseUrl}/employees`, { params: { term } });
  }

  public create(payload: TripUpdate): Observable<ApiResponse<Trip>> {
    return this.httpClient.post<ApiResponse<Trip>>(this.baseUrl, payload);
  }

  public update(id: string, payload: TripUpdate): Observable<ApiResponse<Trip>> {
    return this.httpClient.put<ApiResponse<Trip>>(`${this.baseUrl}/${id}`, payload);
  }

  public submit(id: string): Observable<ApiResponse<Trip>> {
    return this.httpClient.post<ApiResponse<Trip>>(`${this.baseUrl}/${id}/submit`, {});
  }

  public approve(id: string, comment: string | null): Observable<ApiResponse<Trip>> {
    return this.httpClient.post<ApiResponse<Trip>>(`${this.baseUrl}/${id}/approve`, { comment });
  }

  public reject(id: string, comment: string): Observable<ApiResponse<Trip>> {
    return this.httpClient.post<ApiResponse<Trip>>(`${this.baseUrl}/${id}/reject`, { comment });
  }

  public delete(id: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}/${id}`);
  }
}
